import { Link } from "react-router-dom";
import { MapPin, Check, ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow, EstimateButton, CallButton } from "@/components/Shared";
import { LOCATIONS } from "@/data/locations";
import { SERVICES } from "@/data/content";
import { useSEO } from "@/hooks/useSEO";

export default function LocationPage({ location }) {
  const loc = location || LOCATIONS[0];

  useSEO(
    `Home Renovation in ${loc.name} | MTC Home Renovation`,
    `MTC Home Renovation provides kitchen, bathroom, basement, flooring and complete home renovations in ${loc.name}, Ontario. Licensed & insured. Free estimates: (519) 760-0444.`
  );

  return (
    <>
      <section className="py-16 sm:py-24 border-b border-obsidian/10" data-testid="location-hero">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <Reveal>
            <Eyebrow>Service Area · {loc.region}</Eyebrow>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl font-medium tracking-tight leading-[1.1] text-obsidian max-w-2xl">
              Home renovation in <em className="italic text-teak">{loc.name}.</em>
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-stone-600 font-light leading-relaxed">{loc.intro}</p>
            <p className="mt-5 flex items-center gap-2 text-xs font-mono uppercase tracking-[0.16em] text-stonemuted">
              <MapPin size={14} className="text-teak" /> {loc.housing}
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <EstimateButton testId="location-book-button" />
              <CallButton testId="location-call-button" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-20 border-b border-obsidian/10 bg-parchment/60" data-testid="location-popular">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <Reveal>
            <h2 className="font-display text-2xl sm:text-3xl font-medium tracking-tight text-obsidian">
              Popular projects in {loc.name}
            </h2>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
            {loc.popular.map((p, i) => (
              <Reveal key={p} delay={i * 0.08} className="border border-obsidian/15 bg-white p-6">
                <Check size={18} className="text-teak" />
                <p className="mt-3 text-sm font-medium text-obsidian leading-relaxed">{p}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 border-b border-obsidian/10" data-testid="location-services">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <Reveal>
            <h2 className="font-display text-2xl sm:text-3xl font-medium tracking-tight text-obsidian">
              Our services in {loc.name}
            </h2>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SERVICES.map((s) => (
              <Link
                key={s.id}
                to="/services"
                data-testid={`location-service-${s.id}`}
                className="group flex items-center justify-between border border-obsidian/15 px-5 py-4 hover:border-teak transition-colors duration-300"
              >
                <span className="text-sm font-medium text-obsidian">{s.title}</span>
                <ArrowUpRight size={15} className="text-stonemuted group-hover:text-teak transition-colors duration-300" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-obsidian text-bone" data-testid="location-cta">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8">
          <div>
            <Eyebrow dark>Free Estimate · {loc.name}</Eyebrow>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl font-medium tracking-tight">
              Planning a renovation in {loc.name}?
            </h2>
          </div>
          <EstimateButton testId="location-cta-book" />
        </div>
      </section>
    </>
  );
}
